import { ButtonBase, styled } from '@mui/material';
import { ASSIGNMENT_LIST_TRANSITION_MS } from 'components/Navigation/assignmentListScrollConstants';
import AssignmentNavigationList from 'components/Navigation/AssignmentNavigationList';
import CourseSummary from 'components/Navigation/CourseSummary';
import { NavigationItemKey } from 'components/Navigation/navigation-config';
import {
    getActiveAssignmentId,
    getVisibleNavigationItems,
    isActivePath,
    NavigationSectionKey,
    SECTION_LABELS,
    SECTION_ORDER,
} from 'components/Navigation/navigation-utils';
import NavigationItem from 'components/Navigation/NavigationItem';
import useAssignmentListScrollMode from 'hooks/navigation/useAssignmentListScrollMode';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, IconButton, Logo, Stack, Text } from 'shell/components';
import { getUserFirstNameWithLastInitial } from 'utils/helper';

const NavigationContent = ({
    course,
    currentUser,
    assignments = [],
    expanded,
    items,
    mobile = false,
    onNavigate,
    onToggle,
    pathname,
    role,
}) => {
    const { t } = useTranslation();
    const visibleItems = getVisibleNavigationItems(items, course);
    const utilityItems = visibleItems.filter((item) => item.section === NavigationSectionKey.UTILITY);
    const assignmentsActive = isActivePath(pathname, '/assignments');
    const activeAssignmentId = getActiveAssignmentId(pathname, assignments);
    const shouldShowAssignmentList = role === 'student' && assignmentsActive && assignments.length > 0;
    const [assignmentListMounted, setAssignmentListMounted] = useState(shouldShowAssignmentList);
    const [assignmentListOpen, setAssignmentListOpen] = useState(shouldShowAssignmentList);
    const { assignmentListRef, isWholeNavScrollable, navigationTopRef } = useAssignmentListScrollMode({
        enabled: shouldShowAssignmentList,
        open: assignmentListOpen,
    });

    useEffect(() => {
        let animationFrame;
        let closeTimer;

        if (shouldShowAssignmentList) {
            setAssignmentListMounted(true);
            animationFrame = window.requestAnimationFrame(() => setAssignmentListOpen(true));
        } else {
            setAssignmentListOpen(false);
            closeTimer = window.setTimeout(() => setAssignmentListMounted(false), ASSIGNMENT_LIST_TRANSITION_MS);
        }

        return () => {
            window.cancelAnimationFrame(animationFrame);
            window.clearTimeout(closeTimer);
        };
    }, [shouldShowAssignmentList]);

    return (
        <NavigationContentRoot expanded={expanded}>
            <NavigationHeader expanded={expanded} mobile={mobile}>
                {!mobile && (
                    <NavigationLogoButton
                        aria-label={'TimelyGrader'}
                        expanded={expanded}
                        onClick={() => onNavigate('/')}
                    >
                        <Logo device={'desktop'} />
                    </NavigationLogoButton>
                )}
                <IconButton
                    aria-label={
                        mobile
                            ? t('nav.close-navigation', 'Close navigation')
                            : expanded
                              ? t('nav.minimize-navigation', 'Minimize navigation')
                              : t('nav.expand-navigation', 'Expand navigation')
                    }
                    kind={mobile ? 'exit' : 'sidebar'}
                    size={'medium'}
                    sx={{
                        marginLeft: expanded || mobile ? 'auto' : 0,
                        '& > svg': { fontSize: 20 },
                    }}
                    variant={'square'}
                    onClick={onToggle}
                />
            </NavigationHeader>
            <NavigationTop ref={navigationTopRef}>
                <CourseSummary course={course} expanded={expanded} />
                {SECTION_ORDER.map((section) => {
                    const sectionItems = visibleItems.filter((item) => item.section === section);
                    if (!sectionItems.length) return null;
                    return (
                        <NavigationSection
                            assignmentListActive={section === NavigationSectionKey.COURSEWORK && assignmentListOpen}
                            assignmentListPresent={section === NavigationSectionKey.COURSEWORK && assignmentListMounted}
                            key={section}
                            expanded={expanded}
                            wholeNavScrollable={isWholeNavScrollable}
                        >
                            <SectionLabel expanded={expanded} color={'light'} variant={'p3'}>
                                {t(...SECTION_LABELS[section])}
                            </SectionLabel>
                            {sectionItems.map((item) => {
                                const isAssignmentsItem = item.key === NavigationItemKey.ASSIGNMENTS;
                                const hasActiveAssignmentChild =
                                    role === 'student' &&
                                    isAssignmentsItem &&
                                    assignmentsActive &&
                                    Boolean(activeAssignmentId);
                                const navigationItem = (
                                    <NavigationItem
                                        key={item.key}
                                        active={hasActiveAssignmentChild ? false : isActivePath(pathname, item.link)}
                                        current={isAssignmentsItem && assignmentsActive && !hasActiveAssignmentChild}
                                        emphasized={hasActiveAssignmentChild}
                                        expanded={expanded}
                                        item={item}
                                        onClick={() => onNavigate(item.link)}
                                    />
                                );

                                return isAssignmentsItem ? (
                                    <AssignmentNavigationGroup key={item.key} expanded={expanded}>
                                        {navigationItem}
                                        {assignmentListMounted && (
                                            <AssignmentNavigationList
                                                assignments={assignments}
                                                containerRef={assignmentListRef}
                                                expanded={expanded}
                                                onNavigate={onNavigate}
                                                open={assignmentListOpen}
                                                pathname={pathname}
                                                scrollable={!isWholeNavScrollable}
                                            />
                                        )}
                                    </AssignmentNavigationGroup>
                                ) : (
                                    navigationItem
                                );
                            })}
                        </NavigationSection>
                    );
                })}
                <NavigationSection key='utility' expanded={expanded} utility>
                    {utilityItems.map((item) => (
                        <NavigationItem
                            key={item.key}
                            active={isActivePath(pathname, item.link)}
                            expanded={expanded}
                            item={item}
                            onClick={() => onNavigate(item.link)}
                        />
                    ))}
                </NavigationSection>
            </NavigationTop>
            <NavigationBottom>
                {currentUser && (
                    <ProfileArea expanded={expanded}>
                        <ProfileButton
                            aria-label={t('nav.open-settings', 'Open settings')}
                            expanded={expanded}
                            onClick={() => onNavigate('/settings')}
                        >
                            <ProfileAvatar size={'medium'} user={currentUser} />
                            <ProfileName expanded={expanded} color={'primary'} maxLines={2} variant={'p1'}>
                                {getUserFirstNameWithLastInitial(currentUser)}
                            </ProfileName>
                        </ProfileButton>
                    </ProfileArea>
                )}
            </NavigationBottom>
        </NavigationContentRoot>
    );
};

const NavigationContentRoot = styled(Stack)(({ expanded, theme }) => ({
    height: '100%',
    width: '100%',
    alignItems: expanded ? 'stretch' : 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    overflow: 'hidden',
}));

const NavigationTop = styled(Stack)(({ expanded, theme }) => ({
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    width: '100%',
    paddingTop: theme.spacing(1.5),
    alignItems: expanded ? 'stretch' : 'center',
    gap: theme.spacing(3.5),
}));

const NavigationHeader = styled(Stack)(({ expanded, mobile, theme }) => ({
    flexShrink: 0,
    width: '100%',
    minHeight: 46,
    padding: mobile ? 0 : theme.spacing(2),
    paddingLeft: theme.spacing(4),
    alignItems: 'center',
    justifyContent: expanded && !mobile ? 'space-between' : mobile ? 'flex-end' : 'center',
    flexDirection: 'row',
}));

const NavigationLogoButton = styled(ButtonBase, {
    shouldForwardProp: (prop) => prop !== 'expanded',
})(({ expanded }) => ({
    width: expanded ? 106 : 0,
    minWidth: 0,
    height: 24,
    overflow: 'hidden',
    opacity: expanded ? 1 : 0,
    visibility: expanded ? 'visible' : 'hidden',
    pointerEvents: expanded ? 'auto' : 'none',
    transition: 'width 160ms ease, opacity 80ms ease, visibility 0s linear',
    transitionDelay: expanded ? '0ms, 100ms, 100ms' : '0ms, 0ms, 0ms',
    '& svg': { width: 106, height: 'auto' },
}));

const NavigationSection = styled(Stack, {
    shouldForwardProp: (prop) =>
        !['assignmentListActive', 'assignmentListPresent', 'utility', 'wholeNavScrollable'].includes(prop),
})(({ assignmentListActive, assignmentListPresent, expanded, theme, utility, wholeNavScrollable }) => ({
    flexGrow: assignmentListActive && !wholeNavScrollable ? 1 : 0,
    flexShrink: assignmentListPresent && !wholeNavScrollable ? 1 : 0,
    minHeight: assignmentListPresent && !wholeNavScrollable ? 0 : undefined,
    overflow: assignmentListPresent && !wholeNavScrollable ? 'hidden' : 'visible',
    width: expanded ? '100%' : 74,
    alignItems: expanded ? 'stretch' : 'center',
    gap: theme.spacing(1),
    paddingInline: expanded ? theme.spacing(4) : 0,
    ...(utility && { marginTop: 'auto' }),
}));

const AssignmentNavigationGroup = styled(Stack)(({ expanded }) => ({
    width: '100%',
    flex: '1 1 auto',
    minHeight: 0,
    alignItems: expanded ? 'stretch' : 'center',
}));

const SectionLabel = styled(Text)(({ expanded }) => ({
    flexShrink: 0,
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: expanded ? 'left' : 'center',
}));

const NavigationBottom = styled(Stack)(({ expanded, theme }) => ({
    flexShrink: 0,
    width: '100%',
    alignItems: expanded ? 'stretch' : 'center',
    gap: theme.spacing(2),
}));

const ProfileArea = styled(Box)(({ expanded, theme }) => ({
    width: '100%',
    padding: theme.spacing(4),
    borderTop: `1px solid ${theme.palette.ensightful.neutral[200]}`,
    '& > div': { width: expanded ? '100%' : 'auto' },
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
    border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    filter: 'drop-shadow(0 1px 6px rgba(0, 0, 0, 0.10))',
}));

const ProfileButton = styled(ButtonBase)(({ expanded, theme }) => ({
    width: '100%',
    minHeight: 40,
    justifyContent: expanded ? 'flex-start' : 'center',
    gap: expanded ? theme.spacing(2) : 0,
    overflow: 'visible',
    borderRadius: 8,
    '&:focus-visible': { outline: `${theme.palette.ensightful.secondary[400]} solid 3px`, outlineOffset: 2 },
}));

const ProfileName = styled(Text, {
    shouldForwardProp: (prop) => prop !== 'expanded',
})(({ expanded }) => ({
    minWidth: 0,
    flex: expanded ? '1 1 auto' : '0 0 0',
    overflow: 'hidden',
    textAlign: 'left',
    opacity: expanded ? 1 : 0,
    visibility: expanded ? 'visible' : 'hidden',
    pointerEvents: expanded ? 'auto' : 'none',
    transition: 'opacity 80ms ease, visibility 0s linear',
    transitionDelay: expanded ? '100ms, 100ms' : '0ms, 0ms',
}));

export default NavigationContent;
